import { AsteriskNotification } from "Components/AsteriskNotification";
import { BulmaNavButtons } from "Components/BulmaNavButtons";
import { Dividers } from "Components/Dividers";
import { useDocumentTitle } from "Components/Hooks";
import { Horizontal } from "Components/Horizontal";
import { Section } from "Components/Section";
import { WhatIsThisModal } from "Components/WhatIsThisModal";
import { RadioYesNo } from "Inputs/Radio";
import {
  FitnessInstructorOptionalCoveragesModel,
  MedicalPaymentsCoverage,
} from "SharedConstants/Schema";
import { formatPercent, formatWholeDollars } from "Tools";
import { AbuseCoverageSection } from "Views/Shared/AbuseCoverageSection";
import { NohaCoverageSection } from "Views/Shared/NohaCoverageSection";
import { Heading } from "../../Components/Heading";
import { MVCForm, MVCFormServerProps } from "../MVCForm";
import { ModelStateValues } from "../Tools";

export function OptionalCoverages(
  props: FitnessInstructorOptionalCoveragesModel & {
    formProps: MVCFormServerProps;
  },
) {
  const getInitialValues = (values: ModelStateValues) => {
    return {
      ...values,
      ...props.NohaCoverageModel,
      ...props.AbuseCoverageFieldsetModel,
    };
  };

  const title = "Optional Coverages";
  useDocumentTitle(`${title} - ${props.TitleProps.PageTitleSuffix}`);
  return (
    <>
      <Heading {...props} title={title} />
      <MVCForm
        {...props.formProps}
        initialValues={getInitialValues(props.formProps.initialValues)}
      >
        {({ isSubmitting }) => (
          <div className="narrowFieldBodies3">
            <AsteriskNotification />
            <Dividers>
              <NohaCoverageSection {...props.NohaCoverageModel} />
              <MedicalPaymentsCoverageSection
                {...props.OptionalMedicalPaymentsCoverage}
              />
              <AbuseCoverageSection {...props.AbuseCoverageFieldsetModel} />
              <OtherCoverageSection />
            </Dividers>
            <BulmaNavButtons
              backCaption="Back"
              continueCaption="Continue"
              isDisabled={isSubmitting}
            />
          </div>
        )}
      </MVCForm>
    </>
  );
}

function MedicalPaymentsCoverageSection(props: MedicalPaymentsCoverage) {
  return (
    <Section
      title={
        <>
          Medical Payments Coverage{" "}
          <WhatIsThisModal
            modalTitle={`${formatWholeDollars(
              props.Limit,
            )} Medical Payments Coverage`}
          >
            This coverage will reimburse an injured spectator or guest for
            medical and/or funeral expenses incurred as a result of bodily
            injury or death, regardless of whether you are liable or not.
          </WhatIsThisModal>
        </>
      }
    >
      <div style={{ margin: "auto", maxWidth: "75%" }}>
        <RadioYesNo fieldName="DoesWantMedicalExpense">
          <div>
            Would you like to add {formatWholeDollars(props.Limit)} Medical
            Payments Coverage for an additional{" "}
            <strong>
              {formatPercent(props.PremiumRate, 0)} of Liability Premium
            </strong>
            ?
          </div>
        </RadioYesNo>
      </div>
    </Section>
  );
}

function OtherCoverageSection() {
  return (
    <Section title="Other Optional Coverages">
      <Horizontal isRequired label="Higher per Occurrence" shouldNotPadLabel>
        <RadioYesNo fieldName="DoesWantHigherOccurrenceCoverage">
          <div>
            <div className="has-text-weight-bold">
              Would you like to apply for a higher Per Occurrence amount, up to
              $4,000,000?
            </div>
            <div className="is-italic">
              Note that this coverage is subject to Additional Underwriting and
              cannot be automatically bound.
            </div>
          </div>
        </RadioYesNo>
      </Horizontal>
      <Horizontal isRequired label="Equipment" shouldNotPadLabel>
        <RadioYesNo fieldName="DoesWantHigherEquipmentCoverage">
          <div>
            <div className="has-text-weight-bold">
              Would you like to apply for Equipment Coverage with limits up to
              $750,000?
            </div>
            <div className="is-italic">
              Note that this coverage is subject to Additional Underwriting and
              cannot be automatically bound.
            </div>
          </div>
        </RadioYesNo>
      </Horizontal>
    </Section>
  );
}
